import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  Button,
  ButtonProps,
  DropdownNext,
  DropdownNextOptionProps,
  Text,
} from '@desygner/ui-common-components';

import { StyledInputWrapper, StyledWrapper } from './ZoomManager.style';

export interface ZoomManagerProps {
  initialValue?: number;
  fitZoom?: number;
  fillZoom?: number;
  step?: number;
  options: DropdownNextOptionProps[];
  onZoomChange: (value: string | number) => void;
  min?: number;
  max?: number;
  size?: ButtonProps['size'];
  disablePortal?: boolean;
}

export const ZoomManager = ({
  initialValue = 100,
  fitZoom = 100,
  fillZoom = 120,
  step = 20,
  options,
  onZoomChange,
  min = 10,
  max = 1000,
  size = 'Medium',
  disablePortal = false,
}: ZoomManagerProps) => {
  const [value, setValue] = useState(initialValue ?? 100);
  const fitZoomLabel = useMemo(
    () => options.find((option) => option.value === 'fit')?.label || 'Fit',
    [options]
  );

  const fillZoomLabel = useMemo(
    () => options.find((option) => option.value === 'fill')?.label || 'Fill',
    [options]
  );

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  const handleChange = useCallback(
    (value: string | number) => {
      if (value === 'fit') {
        onZoomChange('fit');
        return;
      }

      if (value === 'fill') {
        onZoomChange('fill');
        return;
      }

      const newValue = typeof value === 'string' ? Number(value) : value;

      if (newValue > max || newValue < min || !newValue) return;

      setValue(newValue);
      onZoomChange(value);
    },
    [max, min, onZoomChange]
  );

  const computedValue = useMemo(() => {
    if (value === fitZoom) {
      return fitZoomLabel;
    }

    if (value === fillZoom) {
      return fillZoomLabel;
    }

    return `${value}%`;
  }, [fillZoom, fillZoomLabel, fitZoom, fitZoomLabel, value]);

  return (
    <StyledWrapper>
      <Button
        icon={{
          name: 'editor-minus',
        }}
        action="zoom-out"
        size={size}
        variant="ghost"
        onClick={() => handleChange(value - step)}
      />

      <DropdownNext
        getPopupContainer={
          disablePortal ? (trigger) => trigger.parentElement || document.body : undefined
        }
        options={options}
        onClick={handleChange}
        trigger={'click'}
      >
        <StyledInputWrapper>
          <Text size="Body">{computedValue}</Text>
        </StyledInputWrapper>
      </DropdownNext>

      <Button
        icon={{
          name: 'editor-plus',
        }}
        action="zoom-in"
        size={size}
        variant="ghost"
        onClick={() => handleChange(value + step)}
      />
    </StyledWrapper>
  );
};
